import PaginationComponentSkeleton from './Skeleton';
import { getPaginationItems } from './helper';
import { NewPaginationProps } from './props';
import styles from './styles.module.scss';

/**
 * PaginationComponent
 * A component that renders a pagination control with page numbers and navigation buttons.
 * Handles loading states, page navigation, and supports different styling variants.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.currentPage - The currently active page number
 * @param {number} props.lastPage - The total number of pages
 * @param {number} props.maxLength - Maximum number of pagination items to show
 * @param {(page: number) => void} props.setCurrentPage - Callback to set the current page
 * @param {boolean} props.hasNextPage - Whether there is a next page available
 * @param {boolean} props.hasPreviousPage - Whether there is a previous page available
 * @param {boolean} props.loading - Whether the pagination is in loading state
 * @param {() => void} props.fetchNextPage - Callback to fetch the next page
 * @param {() => void} props.fetchPreviousPage - Callback to fetch the previous page
 * @param {boolean} props.secondaryStyles - Whether to use secondary styling variant
 * @returns {JSX.Element} The rendered pagination control
 *
 * @example
 * // Basic usage
 * <PaginationComponent
 *   currentPage={1}
 *   lastPage={10}
 *   maxLength={7}
 *   setCurrentPage={(page) => setPage(page)}
 *   hasNextPage={true}
 *   hasPreviousPage={false}
 *   loading={false}
 *   fetchNextPage={() => fetchNext()}
 *   fetchPreviousPage={() => fetchPrev()}
 *   secondaryStyles={false}
 * />
 */
export function PaginationComponent({
  currentPage,
  lastPage,
  maxLength,
  setCurrentPage,
  hasNextPage,
  hasPreviousPage,
  loading,
  fetchNextPage,
  fetchPreviousPage,
  secondaryStyles,
}: NewPaginationProps): JSX.Element {
  const pageNums = getPaginationItems(currentPage, lastPage, maxLength);

  if (loading) {
    return <PaginationComponentSkeleton secondaryStyles={secondaryStyles} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__content}>
        <button
          className={`${styles.container__content__page} ${secondaryStyles ? styles['container__content__page--secondary'] : ''}`}
          disabled={!hasPreviousPage}
          onClick={fetchPreviousPage}
        >
          <i className="fas fa-chevron-left" />
        </button>
        {pageNums.map((item: number, index) =>
          !isNaN(item) ? (
            <button
              className={`${styles.container__content__page} ${secondaryStyles ? styles['container__content__page--secondary'] : ''} ${item == currentPage ? styles['container__content__page--active'] : ''}`}
              key={`pagination-icon-skeleton-${item}+${index}`}
              onClick={() => setCurrentPage(item)}
            >
              {item}
            </button>
          ) : (
            '...'
          ),
        )}
        <button
          className={`${styles.container__content__page} ${secondaryStyles ? styles['container__content__page--secondary'] : ''}`}
          disabled={!hasNextPage}
          onClick={fetchNextPage}
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}
